import { format } from 'date-fns'
import { useCallback } from 'react'

import { Location } from '@/features/location'
import { useReservation } from '@/features/reservation'
import { ULR_QUERY_PARAM } from '@/features/url-handling'
import { useAppConfigStore } from '@/stores/app-store-config/appConfigStore'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { CartRoom, CartStoreSlices } from '@/stores/cart-store/slices/types'
import { getAdvertisementCode } from '@/utils/getAdvertisementCode'

const TRACKING_KEYS = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_content',
	'utm_term',
	'wh_token',
]

const getLangForPayload = (lang: string) => {
	if (['de', 'en', 'it'].includes(lang)) {
		return lang
	}

	return 'en'
}

const mapPromoCode = (promotionCode?: string) => {
	if (!promotionCode) return { code: '' }

	return { code: promotionCode }
}

const getChildrenObject = (children?: number[]) => {
	const childrenObj: Record<string, string> = {}

	children?.forEach((child, index) => {
		childrenObj[String(index)] = String(child)
	})

	return childrenObj
}

const getTrackingKeys = () => {
	const searchParams = new URLSearchParams(window.location.search)
	const trackingParams = new URLSearchParams()

	TRACKING_KEYS.forEach((key) => {
		const value = searchParams.get(key)
		if (!value) return

		trackingParams.append(key, value)
	})

	const paramString = trackingParams.toString()
	if (!paramString.length) return

	return trackingParams.toString()
}

const getServiceObject = (nights: number, services?: CartRoom['services']) => {
	const serviceObj: Record<
		string,
		{
			quantity: number
		}
	> = {}

	services?.forEach((service) => {
		serviceObj[String(service.serviceCode)] = {
			quantity: service.servicePricing === 'Per night' ? nights : 1,
		}
	})

	return serviceObj
}

const mapSelectedRoomsToOptirez = (cart: CartStoreSlices) => {
	const rooms = cart.roomSelection.selectedRooms.map((currentRoom) => {
		const matchingRoomConfiguration = cart.roomConfigurations.find(
			(config) => config.id === currentRoom.roomConfigId
		)

		return {
			arrival: format(cart.travelPeriod.startDate!, 'yyyy-MM-dd'),
			departure: format(cart.travelPeriod.endDate!, 'yyyy-MM-dd'),
			nr_nights: cart.travelPeriod.nights,
			ttl_persons: matchingRoomConfiguration?.adults,
			nr_adults: matchingRoomConfiguration?.adults,
			nr_children: matchingRoomConfiguration?.children.length,
			children_ages: getChildrenObject(matchingRoomConfiguration?.children),
			extras: getServiceObject(cart.travelPeriod.nights, currentRoom.room.services),
			room_code: currentRoom.room.roomTypeCode,
			rate_code: currentRoom.room.rate.ratePlanCode,
		}
	})

	return rooms.reduce(
		(obj, room, index) => {
			obj[String(index + 1)] = room
			return obj
		},
		{} as Record<string, (typeof rooms)[number]>
	)
}

const mapCartStoreToOptirez = (cart: CartStoreSlices, language: string) => {
	const advertisementCode = getAdvertisementCode()

	return {
		backLink: window.location.href,
		successPage:
			cart.location?.successUrl + (advertisementCode ? `?wc=${advertisementCode}` : ''),
		lang: getLangForPayload(language),
		nr_rooms: String(cart.roomSelection.selectedRooms.length),
		Hotel: {
			code: cart.location?.hotelCode,
		},
		arrival: format(cart.travelPeriod.startDate!, 'yyyy-MM-dd'),
		departure: format(cart.travelPeriod.endDate!, 'yyyy-MM-dd'),
		promotion_code: mapPromoCode(cart.promotion.code),
		rooms: mapSelectedRoomsToOptirez(cart),
	}
}

const buildFormAction = (location: Location) => {
	const hotelCode = location.hotelCodePrefix
		? location.hotelCodePrefix + location.hotelCode
		: location.hotelCode

	const trackingParams = getTrackingKeys()
	const advertisementCode = getAdvertisementCode()
	const url = `${location.checkoutUrl}/?hotel_code=${hotelCode}&referrer=website${advertisementCode ? `&wc=${advertisementCode}` : ''}${trackingParams ? `&${trackingParams}` : ''}`

	return url
}

export const useOldBookingCompletion = () => {
	const handleOldBookingCompletion = useCallback(() => {
		const cart = useCartStore.getState()
		if (!cart.location) return

		const { language } = useAppConfigStore.getState()
		const payload = mapCartStoreToOptirez(cart, language)

		// Create hidden form
		const form = document.createElement('form')
		form.style.display = 'none'
		form.method = 'POST'
		form.action = buildFormAction(cart.location)

		// Create hidden input named "Query" with the stringified payload
		const input = document.createElement('input')
		input.name = 'Query'
		input.type = 'hidden'
		input.value = JSON.stringify(payload)

		// Inject to DOM
		form.appendChild(input)
		document.documentElement.appendChild(form)

		// Submit and navigate to form action
		form.submit()
	}, [])

	return { handleOldBookingCompletion }
}

export const useBookingCompletion = () => {
	const { data } = useReservation()

	const handleBookingCompletion = useCallback(() => {
		const { location } = useCartStore.getState()
		const { language } = useAppConfigStore.getState()
		if (!location || !data) return

		const backLinkUrl = new URL(window.location.href)
		const searchParams = backLinkUrl.searchParams
		const query = searchParams.get(ULR_QUERY_PARAM)
		if (!query) return

		const advertisementCode = getAdvertisementCode()

		const successUrl = new URL(location.successUrl)
		successUrl.searchParams.set('language', language)
		if (advertisementCode) successUrl.searchParams.set('wc', advertisementCode)

		const trackingParams = getTrackingKeys()

		searchParams.set(
			ULR_QUERY_PARAM,
			encodeURIComponent(
				JSON.stringify({
					...JSON.parse(decodeURIComponent(query)),
					reservationId: data.reservationId,
				})
			)
		)

		window.location.assign(
			`${location.checkoutUrl}?conf_nr=${data.confirmationNumber}&email=${data.customer.email}&successPage=${encodeURIComponent(successUrl.toString())}&backLink=${encodeURIComponent(backLinkUrl.toString())}${trackingParams ? `&${trackingParams}` : ''}`
		)
	}, [data])

	return { handleBookingCompletion }
}
